import { DatabaseService } from "../database/database-service";
import { EventListenerHandler, isPluginWebInfo, type PluginWebInfo } from "@saludio/core";
import { PluginSettingsStorage } from "./plugin-settings-storage";
import { PluginStatusSet, type PluginEntry } from "../../types";

let pluginServiceInstance: PluginService;

class PluginService {
  private _changeListenerHandler = new EventListenerHandler<Array<PluginEntry>>();
  private _pluginEntryMap = new Map<string, PluginEntry>();

  constructor() {
    this.init();
  }

  addChangeListener(listener: (pluginEntries: Array<PluginEntry>) => void): void {
    this._changeListenerHandler.add(listener);
    // Immediately notify with current state
    listener(Array.from(this._pluginEntryMap.values()));
  }

  removeChangeListener = this._changeListenerHandler.remove.bind(this._changeListenerHandler);

  private async init() {
    // Load storage entries
    const storageEntries = PluginSettingsStorage.getDefs();
    storageEntries
      .map((sE) => ({ ...sE, status: sE.enabled ? PluginStatusSet.LOADING : PluginStatusSet.DISABLED }))
      .forEach((pE) => {
        this._pluginEntryMap.set(pE.id, { ...pE });
      });
    this.updatePluginEntries();

    // Load plugin web info
    const pluginWebInfos = await this.loadPluginwebInfo();
    pluginWebInfos.forEach((pWI) => {
      const pE =
        this._pluginEntryMap.get(pWI.id) ||
        ({
          id: pWI.id,
          name: pWI.name,
          enabled: false,
          status: PluginStatusSet.DISABLED,
        } as PluginEntry);
      pE.webInfo = pWI;
      this._pluginEntryMap.set(pE.id, { ...pE });
    });
    this.updatePluginEntries();

    // Load plugins
    await this.loadPlugins(Array.from(this._pluginEntryMap.values()));

    // Final plugin check
    if (this.markFailedPluginEntries()) {
      this.updatePluginEntries();
    }
  }

  private updatePluginEntries() {
    this._changeListenerHandler.notify(Array.from(this._pluginEntryMap.values()));
  }

  private async loadPluginwebInfo(): Promise<Array<PluginWebInfo>> {
    const response = await fetch("/plugins/plugins.json");
    if (!response.ok) {
      throw new Error(`Failed to load plugins: ${response.statusText}`);
    }
    const pluginWebInfos = await response.json();
    if (!Array.isArray(pluginWebInfos)) {
      throw new Error("Plugin web info is invalid");
    }
    return pluginWebInfos.filter(isPluginWebInfo);
  }

  private async loadPlugins(pluginEntries: Array<PluginEntry>): Promise<void> {
    await Promise.allSettled(
      pluginEntries.filter((pE) => typeof pE.webInfo !== undefined && pE.enabled).map((pE) => this.loadPlugin(pE)),
    );
  }

  private async loadPlugin(pluginEntry: PluginEntry): Promise<void> {
    try {
      const module = await import(`/plugins/${pluginEntry.webInfo!.jsfile}`);
      if (pluginEntry.webInfo!.cssfile) {
        const styleLinkElement = document.createElement("link");
        styleLinkElement.setAttribute("rel", "stylesheet");
        styleLinkElement.setAttribute("href", `/plugins/${pluginEntry.webInfo!.cssfile}`);
        document.head.appendChild(styleLinkElement);
      }
      pluginEntry.registration = module.default;
      await pluginEntry.registration!.init({
        database: DatabaseService.forPlugin(pluginEntry.id),
      });
      pluginEntry.status = PluginStatusSet.LOADED;
      console.log(`Loaded plugin module: ${pluginEntry.name}`);
    } catch (err) {
      console.error(`Failed to load plugin ${pluginEntry.name}:`, err);
      pluginEntry.status = PluginStatusSet.FAILED;
    }
    this._pluginEntryMap.set(pluginEntry.id, { ...pluginEntry });
    this.updatePluginEntries();
  }

  private markFailedPluginEntries() {
    let failedEntries: Array<string> = [];
    this._pluginEntryMap.forEach((pE) => {
      if (
        (typeof pE.webInfo === undefined || typeof pE.registration === undefined) &&
        pE.status !== PluginStatusSet.FAILED
      ) {
        failedEntries.push(pE.id);
      }
    });
    if (!failedEntries.length) {
      return false;
    }
    failedEntries.forEach((fE) => {
      const failedPE = this._pluginEntryMap.get(fE);
      if (failedPE) {
        failedPE.status = PluginStatusSet.FAILED;
        this._pluginEntryMap.set(failedPE.id, { ...failedPE });
      }
    });
  }

  getAllPluginIds(): string[] {
    return Array.from(this._pluginEntryMap.keys());
  }

  getPluginEntry(pluginId: string): PluginEntry | undefined {
    return this._pluginEntryMap.get(pluginId);
  }

  togglePlugin(pluginId: string): boolean {
    const pluginEntry = this._pluginEntryMap.get(pluginId);
    if (!pluginEntry) {
      return false;
    }

    pluginEntry.enabled = !pluginEntry.enabled;
    if (
      (pluginEntry.status === PluginStatusSet.DISABLED || pluginEntry.status === PluginStatusSet.FAILED) &&
      typeof pluginEntry.webInfo !== undefined
    ) {
      this.loadPlugin(pluginEntry);
    }

    this._pluginEntryMap.set(pluginEntry.id, { ...pluginEntry });
    PluginSettingsStorage.setDefs(Array.from(this._pluginEntryMap.values()));
    this.updatePluginEntries();
    return true;
  }

  sortPluginEntries(pluginEntries: Array<PluginEntry>) {
    return pluginEntries.sort((pEA, pEB) => pEA.name.localeCompare(pEB.name));
  }
}

export function getPluginService(): PluginService {
  if (!pluginServiceInstance) {
    pluginServiceInstance = new PluginService();
  }
  return pluginServiceInstance;
}
